/**
 * VehicleRequestListPage — Issue #365.
 *
 * Lists vehicle requests and supports draft creation, submit, approve, reject,
 * cancel and complete actions through the backend state machine.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAffairsPermissions';
import {
  ADMIN_AFFAIR_REQUEST_STATUS_LABELS,
  useAdminVehicleRequestMutations,
  useAdminVehicleRequests,
  type AdminVehicleRequestDto,
} from '@/hooks/useAdminAffairs';

const STATUS_BADGE: Readonly<Record<AdminVehicleRequestDto['status'], string>> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

export default function VehicleRequestListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.READ);
  const canWrite = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.WRITE);
  const canApprove = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.APPROVE,
  );

  const { items, total, loading, error, fetchVehicleRequests } =
    useAdminVehicleRequests();
  const {
    loading: actionLoading,
    error: actionError,
    create,
    action,
  } = useAdminVehicleRequestMutations();

  const [statusFilter, setStatusFilter] = useState<
    AdminVehicleRequestDto['status'] | ''
  >('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    startAt: '',
    endAt: '',
    destination: '',
    purpose: '',
    passengers: '' as string,
  });

  useEffect(() => {
    if (!canRead) return;
    void fetchVehicleRequests({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchVehicleRequests]);

  if (!canRead) return <AccessDenied resource="用车申请" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">用车申请</h1>
          <p className="text-sm text-slate-500">
            用车申请走 draft → submitted → approved → completed
            审批流；行政审批不替代财务报销与油卡结算。
          </p>
        </div>
        {canWrite ? (
          <button
            type="button"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="vehicle-create-entry"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? '取消' : '新增用车申请 →'}
          </button>
        ) : null}
      </header>

      {creating && canWrite ? (
        <Card className="space-y-3 p-4">
          <label className="block text-sm">
            开始时间
            <input
              type="datetime-local"
              className="mt-1 block w-64 rounded border border-slate-300 px-2 py-1"
              value={form.startAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, startAt: e.target.value }))
              }
              data-testid="vehicle-create-start"
            />
          </label>
          <label className="block text-sm">
            结束时间
            <input
              type="datetime-local"
              className="mt-1 block w-64 rounded border border-slate-300 px-2 py-1"
              value={form.endAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, endAt: e.target.value }))
              }
              data-testid="vehicle-create-end"
            />
          </label>
          <label className="block text-sm">
            目的地
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.destination}
              onChange={(e) =>
                setForm((f) => ({ ...f, destination: e.target.value }))
              }
              data-testid="vehicle-create-destination"
            />
          </label>
          <label className="block text-sm">
            事由
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.purpose}
              onChange={(e) =>
                setForm((f) => ({ ...f, purpose: e.target.value }))
              }
              data-testid="vehicle-create-purpose"
            />
          </label>
          <label className="block text-sm">
            乘员人数
            <input
              type="number"
              min={1}
              className="mt-1 block w-32 rounded border border-slate-300 px-2 py-1"
              value={form.passengers}
              onChange={(e) =>
                setForm((f) => ({ ...f, passengers: e.target.value }))
              }
            />
          </label>
          <button
            type="button"
            disabled={actionLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
            data-testid="vehicle-create-submit"
            onClick={async () => {
              const created = await create({
                startAt: new Date(form.startAt).toISOString(),
                endAt: new Date(form.endAt).toISOString(),
                destination: form.destination,
                purpose: form.purpose,
                passengers: form.passengers ? Number(form.passengers) : undefined,
              });
              if (created) {
                setForm({
                  startAt: '',
                  endAt: '',
                  destination: '',
                  purpose: '',
                  passengers: '',
                });
                setCreating(false);
                await fetchVehicleRequests({
                  status: statusFilter || undefined,
                });
              }
            }}
          >
            {actionLoading ? '提交中…' : '创建草稿'}
          </button>
          {actionError ? (
            <p
              className="text-sm text-red-600"
              data-testid="vehicle-create-error"
            >
              {actionError.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                (e.target.value as AdminVehicleRequestDto['status']) || '',
              )
            }
            data-testid="vehicle-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_AFFAIR_REQUEST_STATUS_LABELS) as [
                AdminVehicleRequestDto['status'],
                string,
              ][]
            ).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">编号</th>
              <th className="px-3 py-2">目的地</th>
              <th className="px-3 py-2">开始</th>
              <th className="px-3 py-2">结束</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={6}
                  data-testid="vehicle-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={6}
                  data-testid="vehicle-empty"
                >
                  暂无用车申请
                </td>
              </tr>
            ) : (
              items.map((v) => (
                <tr key={v.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">
                    {v.vehicleRequestNo}
                  </td>
                  <td className="px-3 py-2">{v.destination}</td>
                  <td className="px-3 py-2 text-xs">{fmtDateTime(v.startAt)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDateTime(v.endAt)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE[v.status]}`}
                    >
                      {ADMIN_AFFAIR_REQUEST_STATUS_LABELS[v.status]}
                    </span>
                  </td>
                  <td className="space-x-2 px-3 py-2 text-xs">
                    {v.status === 'draft' && canWrite ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        data-testid={`vehicle-submit-${v.id}`}
                        onClick={async () => {
                          await action(v.id, 'submit', v.version);
                          await fetchVehicleRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        提交审批
                      </button>
                    ) : null}
                    {v.status === 'submitted' && canApprove ? (
                      <>
                        <button
                          type="button"
                          className="text-emerald-600 hover:underline"
                          data-testid={`vehicle-approve-${v.id}`}
                          onClick={async () => {
                            await action(v.id, 'approve', v.version);
                            await fetchVehicleRequests({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          批准
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          data-testid={`vehicle-reject-${v.id}`}
                          onClick={async () => {
                            const reason = window.prompt('拒绝原因');
                            if (!reason) return;
                            await action(v.id, 'reject', v.version, reason);
                            await fetchVehicleRequests({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          拒绝
                        </button>
                      </>
                    ) : null}
                    {v.status === 'approved' && canApprove ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        data-testid={`vehicle-complete-${v.id}`}
                        onClick={async () => {
                          await action(v.id, 'complete', v.version);
                          await fetchVehicleRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        用车完成
                      </button>
                    ) : null}
                    {(v.status === 'draft' || v.status === 'submitted' ||
                      v.status === 'approved') &&
                    canWrite ? (
                      <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        data-testid={`vehicle-cancel-${v.id}`}
                        onClick={async () => {
                          const reason = window.prompt('取消原因（可选）');
                          await action(
                            v.id,
                            'cancel',
                            v.version,
                            reason ?? undefined,
                          );
                          await fetchVehicleRequests({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        取消
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">合计 {total} 条用车申请</p>
    </div>
  );
}
